export type SellerDisplay = {
  primary: string;
  secondary: string | null;
};

export function formatSignedNumber(value: number | null | undefined): string {
  if (value == null) return '—';
  const formatted = new Intl.NumberFormat('ru-RU').format(value);
  return value > 0 ? `+${formatted}` : formatted;
}

export function formatSignedRub(value: number | null | undefined): string {
  if (value == null) return '—';
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(abs);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

export function sellerDisplay(
  name: string | null | undefined,
  externalId: string | null | undefined,
): SellerDisplay {
  const cleanName = name?.trim() || null;
  const cleanId = externalId?.trim() || null;
  if (cleanName && cleanId && cleanName !== cleanId) {
    return { primary: cleanName, secondary: cleanId };
  }
  if (cleanName) return { primary: cleanName, secondary: null };
  if (cleanId) return { primary: cleanId, secondary: null };
  return { primary: '—', secondary: null };
}

export function jsonFieldLabel(value: unknown): string {
  if (value == null) return 'Нет данных';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

export function productDetailsStatusLabel(source: string | null | undefined): string {
  if (!source || source === 'basic' || source === 'mpstats_minimal') {
    return 'базовая карточка';
  }
  return 'получена';
}

export function section3SourceLabel(source: string | null | undefined): string {
  switch (source) {
    case 'mpstats_brands':
      return 'Агрегат брендов';
    case 'mpstats_sellers':
      return 'Агрегат продавцов';
    case 'new_winner_cards':
      return 'Карточки отчёта';
    case 'warning':
      return 'Диагностика';
    default:
      return source ? 'Сигнал отчёта' : '—';
  }
}

const STAGE_DISPLAY_LABELS: Record<string, string> = {
  starting: 'Запуск',
  quota_check: 'Проверка квот',
  ingest: 'Загрузка данных',
  ingest_done: 'Данные загружены',
  analytics: 'Аналитика событий',
  analytics_done: 'Аналитика завершена',
  bhapi: 'Детализация карточек',
  bhapi_done: 'Детализация завершена',
  llm_events: 'AI-комментарии к карточкам',
  llm_sections: 'AI-сводки разделов 4–7',
  llm_done: 'AI-обработка завершена',
  notify: 'Уведомления',
  success: 'Готово',
  failed: 'Ошибка',
};

const CARD_DETAILS_STAGE = new Set(['bhapi', 'bhapi_done']);
const TECHNICAL_DETAILS_ERROR =
  /bhapi|bhapiitem|pydantic|validation error|availability|input should be a valid string|errors\.pydantic\.dev|parser|парсер/i;

export function stageDisplayName(stage: string | null | undefined): string {
  if (!stage) return '—';
  return STAGE_DISPLAY_LABELS[stage] ?? stage;
}

export function stageErrorForDisplay(
  stage: string | null | undefined,
  error: string | null | undefined,
): string | null {
  if (!error) return null;
  if (CARD_DETAILS_STAGE.has(stage ?? '') && error === 'disabled') {
    return 'Детализация карточек отключена';
  }
  if (CARD_DETAILS_STAGE.has(stage ?? '') || TECHNICAL_DETAILS_ERROR.test(error)) {
    return 'Детализация карточек недоступна';
  }
  return error;
}

export function stageDetailsForDisplay(
  stage: string | null | undefined,
  details: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!details) return null;
  const out: Record<string, unknown> = { ...details };
  const technicalStage = CARD_DETAILS_STAGE.has(stage ?? '');
  if (technicalStage) {
    if (typeof out.reason === 'string') {
      out.reason = stageErrorForDisplay(stage, out.reason) ?? out.reason;
    }
    if (typeof out.error === 'string') {
      out.error = stageErrorForDisplay(stage, out.error) ?? out.error;
    }
    if (Array.isArray(out.errors)) {
      out.errors_count = out.errors.length;
      delete out.errors;
    }
  }
  return out;
}
